import React, { useCallback, useEffect, useRef, useState } from 'react';

export default function TableScroll({ children }) {
  const viewRef = useRef(null);
  const dragRef = useRef(null);
  const [thumb, setThumb] = useState({ show: false, width: 0, left: 0 });

  const measure = useCallback(() => {
    const el = viewRef.current;
    if (!el) return;
    const { clientWidth, scrollWidth, scrollLeft } = el;
    const show = scrollWidth > clientWidth + 1;
    const width = show ? Math.max(36, (clientWidth / scrollWidth) * clientWidth) : 0;
    const maxLeft = Math.max(0, clientWidth - width);
    const range = scrollWidth - clientWidth;
    const left = show && range > 0 ? (scrollLeft / range) * maxLeft : 0;
    setThumb({ show, width, left });
  }, []);

  useEffect(() => {
    const el = viewRef.current;
    if (!el) return undefined;
    measure();
    const observers = [];
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
      observers.push(ro);
    }
    window.addEventListener('resize', measure);
    return () => {
      observers.forEach(ro => ro.disconnect());
      window.removeEventListener('resize', measure);
    };
  }, [measure, children]);

  const scrollToThumbLeft = (nextLeft, width = thumb.width) => {
    const el = viewRef.current;
    if (!el) return;
    const maxLeft = Math.max(0, el.clientWidth - width);
    const clamped = Math.min(maxLeft, Math.max(0, nextLeft));
    const range = el.scrollWidth - el.clientWidth;
    el.scrollLeft = maxLeft <= 0 || range <= 0 ? 0 : (clamped / maxLeft) * range;
  };

  const onThumbPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { startX: event.clientX, startLeft: thumb.left, width: thumb.width };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onThumbPointerMove = (event) => {
    if (!dragRef.current) return;
    const { startX, startLeft, width } = dragRef.current;
    scrollToThumbLeft(startLeft + (event.clientX - startX), width);
  };

  const onThumbPointerUp = () => {
    dragRef.current = null;
  };

  const onTrackPointerDown = (event) => {
    if (event.target !== event.currentTarget) return;
    const rect = event.currentTarget.getBoundingClientRect();
    scrollToThumbLeft(event.clientX - rect.left - thumb.width / 2);
  };

  return (
    <div className="table-scroll">
      <div className="wh-table-wrap" ref={viewRef} onScroll={measure}>
        {children}
      </div>
      {thumb.show ? (
        <div className="table-scroll-track" onPointerDown={onTrackPointerDown}>
          <div
            className="table-scroll-thumb"
            style={{ width: `${thumb.width}px`, transform: `translateX(${thumb.left}px)` }}
            onPointerDown={onThumbPointerDown}
            onPointerMove={onThumbPointerMove}
            onPointerUp={onThumbPointerUp}
            onPointerCancel={onThumbPointerUp}
          />
        </div>
      ) : null}
    </div>
  );
}
